import React, { useState, useEffect, useRef } from 'react';
import { PlayersRepository } from '../services/repository';
import './PlayerAutocomplete.css';

const PlayerAutocomplete = ({ value, onChange, placeholder, disabled = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchPlayers = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      return;
    }

    try {
      setLoading(true);
                  const players = await PlayersRepository.searchPlayers(query);
      setSuggestions(players);
      setShowSuggestions(players.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error searching players:', error);
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue, null); // Clear the selected player ID
    
    if (newValue.length >= 3) {
      searchPlayers(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  const handleSuggestionClick = (player) => {
    const displayName = player.displayName || player.name;
    setInputValue(displayName);
    onChange(displayName, player.id);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="player-autocomplete" ref={wrapperRef}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && (
          <span className="input-group-text">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Ładowanie...</span>
            </div>
          </span>
        )}
      </div>
      
              {showSuggestions && (
        <div className="suggestions-dropdown">
          {suggestions.map((player, index) => (
            <div
              key={player.id}
              className={`suggestion-item ${index === selectedIndex ? 'suggestion-selected' : ''}`}
              onClick={() => handleSuggestionClick(player)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {player.displayName || player.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayerAutocomplete; 